import React from "react";
import { Tooltip } from "@fluentui/react-components";
import { Pencil } from "lucide-react";
import { MilestoneIcon } from "../../views/dashboard/icons/icons";
import { getLabelByType, getTaskPriorityPillClass, getTaskStatusPillClass, TimelineTaskIcons } from "../../helpers/utils";
import { formatDateRange, formatHours, pluralize } from "./cardFormat";
import TruncatedText from "../truncatedText/TruncatedText";
import styles from "./resourceInsights.module.scss";

/**
 * A task a resource is allocated to, as Resource Insights lists it and as the planner lists the tasks a row cannot
 * draw: name, status, priority, flags, progress, where it sits and when. Pressing the task calls `onNavigate`, the
 * pencil calls `onEdit`; a task without `onNavigate` cannot be pressed and one without `onEdit` has no pencil.
 *
 * @param task  `{ id, name, refId, status, priority, progress, isCritical, isMilestone, isOverdue, openRisksCount,
 *   startDate, endDate, project, scope, taskGroup, allocation: { startDate, endDate, hoursPerDay, totalHours } }`
 */
export default function AssignmentRow({ task, showHours, onNavigate, onEdit }) {
  const breadcrumb = [task.project?.name, task.scope?.name, task.taskGroup?.name].filter(Boolean).join(" › ");
  const hasHours = task.allocation && (task.allocation.hoursPerDay > 0 || task.allocation.totalHours > 0);

  const Main = onNavigate ? "button" : "div";
  const mainProps = onNavigate ? { type: "button", onClick: () => onNavigate(task) } : {};

  return (
    <div className={`${styles.assignment} ${onNavigate ? "" : styles.assignmentStatic}`}>
      <Main className={styles.assignmentMain} {...mainProps}>
        <div className={styles.assignmentTitle}>
          <span className={styles.statusIcon}>{TimelineTaskIcons[task.status]?.()}</span>
          <TruncatedText className={styles.assignmentName} text={task.name} />
          {task.refId && <em>{task.refId}</em>}
          {task.isMilestone && (
            <Tooltip content="Milestone" relationship="label" positioning="above">
              <span className={styles.milestoneIcon}>
                <MilestoneIcon size={14} />
              </span>
            </Tooltip>
          )}
        </div>

        <div className={styles.assignmentPills}>
          <span className={getTaskStatusPillClass(task.status)}>{getLabelByType("taskStatus", task.status)}</span>
          {task.priority != null && <span className={getTaskPriorityPillClass(task.priority)}>{getLabelByType("taskPriority", task.priority)}</span>}
          {task.isCritical && <span className="pill AtRisk">Critical</span>}
          {task.isOverdue && <span className="pill AtRisk">Overdue</span>}
          {task.openRisksCount > 0 && <span className="pill OffTrack">{pluralize(task.openRisksCount, "risk")}</span>}
        </div>

        <div className={styles.progressRow}>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${task.progress}%` }} />
          </div>
          <span>{task.progress}%</span>
        </div>

        <div className={styles.assignmentMeta}>
          <TruncatedText text={breadcrumb} />
          <span>{formatDateRange(task.allocation?.startDate ?? task.startDate, task.allocation?.endDate ?? task.endDate)}</span>
          {showHours && <span>{hasHours ? `${formatHours(task.allocation.hoursPerDay)} / day · ${formatHours(task.allocation.totalHours)}` : "Assigned"}</span>}
        </div>
      </Main>

      {onEdit && (
        <Tooltip content={`Edit ${task.name}`} relationship="label" positioning="above">
          <button type="button" className={styles.assignmentEdit} onClick={() => onEdit(task)}>
            <Pencil size={14} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
